import { Link, useSearchParams } from "react-router-dom";
import { Seo } from "../components/Seo";
import { Breadcrumbs, Disclaimer, OperatorBlock } from "../components/Layout";
import { SmartForm, APPLICATION_FIELDS, ENQUIRY_FIELDS, CI_FIELDS, CUSTOMER_CONSENT, GRIEVANCE_FIELDS, useTl } from "../components/Forms";
import { useT } from "../lib/i18n";
import { useSite } from "../lib/site";

function Shell({ title, h1, description, crumbs, intro, children, help = true }) {
  const tl = useTl();
  const { operator } = useSite();
  return (
    <div className="container-x py-12 sm:py-16 max-w-4xl">
      <Seo title={tl(title)} description={tl(description)} crumbs={crumbs} />
      <Breadcrumbs items={crumbs} />
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4" data-testid="page-h1">{tl(h1)}</h1>
      <p className="text-slate-700 mb-8">{tl(intro)}</p>
      {children}
      {help && operator && <div className="mt-10 bg-white border rounded-2xl p-6" data-testid="form-help-contact"><h2 className="text-lg font-bold mb-3">{tl("Need help with this form?")}</h2><OperatorBlock /></div>}
    </div>
  );
}

const CUSTOMER_NOTICE = "We use these details only to respond to your enquiry. If you tick the sharing box, an assigned partner may contact you; otherwise only central staff will. You can ask us to delete your data at any time.";

export function Apply() {
  const [sp] = useSearchParams();
  const { t } = useT();
  return (
    <Shell title="Solar Partner Registration – Apply Online" h1="Apply to Become a Solar Partner" description="Apply to join the partner network. Applications are reviewed manually; approval is not automatic." crumbs={[{ name: "Apply", to: "/apply" }]} intro="Every application is reviewed by a person. Approval is not automatic.">
      <div className="mb-6"><Disclaimer>{t("common.draftNote")}</Disclaimer></div>
      <SmartForm testid="partner-application-form" endpoint="/forms/partner-application" fields={APPLICATION_FIELDS} initial={{ preferred_tier: sp.get("tier") || "" }} submitLabel="Submit application" successText="We will contact you after review." notice="Your application details are used only to assess and process your application." />
    </Shell>
  );
}

export function SolarEnquiry() {
  const tl = useTl();
  return (
    <Shell title="Rooftop Solar Enquiry – Free Site Assessment Request" h1="Get a Rooftop Solar Assessment" description="Tell us about your home or premises; we explain how your details are used and who may contact you." crumbs={[{ name: "Solar Enquiry", to: "/solar-enquiry" }]} intro="For homes and housing societies. Businesses can use the commercial enquiry form.">
      <SmartForm testid="customer-enquiry-form" endpoint="/forms/enquiry" extra={{ kind: "residential" }} fields={[...ENQUIRY_FIELDS, ...CUSTOMER_CONSENT]} submitLabel="Send enquiry" successText="We will call you to discuss next steps." notice={CUSTOMER_NOTICE} />
      <p className="mt-4 text-sm"><Link to="/solar-enquiry/commercial" className="underline text-amber-800">{tl("Commercial or industrial project? Use this form.")}</Link></p>
    </Shell>
  );
}

export function CommercialEnquiry() {
  return (
    <Shell title="Commercial Solar Project Enquiry" h1="Commercial and Industrial Solar Enquiry" description="Share load, roof area and tariff details for an initial C&I solar assessment." crumbs={[{ name: "Solar Enquiry", to: "/solar-enquiry" }, { name: "Commercial", to: "/solar-enquiry/commercial" }]} intro="Share what you know — we will ask for bills and drawings later.">
      <SmartForm testid="ci-enquiry-form" endpoint="/forms/enquiry" extra={{ kind: "commercial" }} fields={[...CI_FIELDS, ...CUSTOMER_CONSENT]} submitLabel="Send project enquiry" successText="We will contact you for an initial assessment." notice={CUSTOMER_NOTICE} />
    </Shell>
  );
}

export function GrievancePage() {
  return (
    <Shell title="Grievance & Complaint Redressal" h1="Grievance and Complaints" description="Raise a complaint, track it with a reference number and see response timelines." crumbs={[{ name: "Grievance", to: "/grievance" }]} intro="You will receive a reference number. We aim to acknowledge complaints within 48 hours. Lead and commission disputes can be raised here too.">
      <SmartForm testid="grievance-form" endpoint="/forms/grievance" fields={GRIEVANCE_FIELDS} submitLabel="Submit complaint" successText="Keep this reference for follow-up." />
    </Shell>
  );
}

export function Contact() {
  const tl = useTl();
  const links = [["/apply", "Apply to become a partner"], ["/solar-enquiry", "Residential solar enquiry"], ["/solar-enquiry/commercial", "Commercial / industrial enquiry"], ["/sign-in", "Existing partner support (log in)"], ["/grievance", "Grievance or complaint"]];
  return (
    <Shell title="Contact – Kota, Rajasthan Office" h1="Contact Us" description="Office address, phone, email and the right form for partner, customer and support questions." crumbs={[{ name: "Contact", to: "/contact" }]} intro="Choose the form that matches your request so it reaches the right team." help={false}>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-white border rounded-2xl p-6 min-w-0" data-testid="contact-business-card"><h2 className="text-xl font-bold mb-4">{tl("Business and contact details")}</h2><OperatorBlock copy /></div>
        <ul className="space-y-3">{links.map(([to, l]) => <li key={to}><Link to={to} className="block bg-white border rounded-xl p-4 font-semibold hover:border-amber-500 transition-colors" data-testid={`contact-link-${to.replace(/\//g, "-").slice(1)}`}>{tl(l)}</Link></li>)}</ul>
      </div>
    </Shell>
  );
}
